import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@material-tailwind/react/Button";
import Icon from "@material-tailwind/react/Icon";
import Dropdown from "@material-tailwind/react/Dropdown";
import DropdownItem from "@material-tailwind/react/DropdownItem";
import { BiChevronDown, BiBell } from "react-icons/bi";

import { Colors } from "../../assets/Colors";
import "./Header.css";
import { checkLoginUser, userSelector } from "../../features/user/userSlice";
import { karyawanSelector } from "../../features/hrd/karyawan/karyawanSlice";
import { useApprovalCount } from "../../hooks/useApprovalCount";

// Label warna per kategori
const CATEGORY_COLOR = {
    "HRD": "bg-purple-100 text-purple-700",
    "Purchase Order": "bg-blue-100 text-blue-700",
    "Purchase Requisition": "bg-blue-100 text-blue-700",
    "Price Comparison": "bg-indigo-100 text-indigo-700",
    "Penerimaan Barang": "bg-green-100 text-green-700",
    "Penerimaan Barang User": "bg-green-100 text-green-700",
    "Pengeluaran Barang": "bg-orange-100 text-orange-700",
    "Return Barang": "bg-yellow-100 text-yellow-700",
    "Jurnal": "bg-teal-100 text-teal-700",
    "Pembayaran Uang Muka": "bg-red-100 text-red-700",
    "Pembayaran AP": "bg-red-100 text-red-700",
    "Pengeluaran Kas HO": "bg-rose-100 text-rose-700",
    "Pengeluaran Kas SO": "bg-rose-100 text-rose-700",
    "Penerimaan Kas HO": "bg-cyan-100 text-cyan-700",
    "Penerimaan Kas SO": "bg-cyan-100 text-cyan-700",
    "Penerimaan Dana": "bg-cyan-100 text-cyan-700",
    "Kendaraan": "bg-gray-100 text-gray-700",
    "Job Request": "bg-amber-100 text-amber-700",
};

const PREVIEW_MAX = 8;

function NotificationPopup({ items, loading, count, onClose, onNavigate }) {
    const preview = items.slice(0, PREVIEW_MAX);
    const remaining = count - preview.length;

    return (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
            {/* Header popup */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-800">
                    {count > 0 ? `${count} item menunggu approval` : "Tidak ada notifikasi"}
                </span>
                {loading && (
                    <svg className="w-4 h-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                )}
            </div>

            {/* List item */}
            <ul className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                {preview.length === 0 && !loading && (
                    <li className="px-4 py-6 text-center text-sm text-gray-400">
                        Semua sudah disetujui
                    </li>
                )}
                {preview.map((item, idx) => {
                    const chipClass = CATEGORY_COLOR[item.label] ?? "bg-gray-100 text-gray-600";
                    return (
                        <li
                            key={`notif-${idx}`}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => onNavigate(item)}
                        >
                            <span className={`mt-0.5 shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${chipClass}`}>
                                {item.label}
                            </span>
                            <span className="text-sm text-gray-700 break-all">{item.title}</span>
                        </li>
                    );
                })}
                {remaining > 0 && (
                    <li
                        className="px-4 py-2 text-center text-xs text-blue-600 font-medium cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => onNavigate({ path: "/approval-list", state: null })}
                    >
                        +{remaining} item lainnya...
                    </li>
                )}
            </ul>

            {/* Footer */}
            <div className="border-t border-gray-100">
                <button
                    type="button"
                    className="w-full py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors"
                    onClick={() => onNavigate({ path: "/approval-list", state: null })}
                >
                    Lihat Semua Approval
                </button>
            </div>
        </div>
    );
}

export default function Header({ showSidebar, setShowSidebar }) {
    const navigate = useNavigate();
    const user = useSelector(userSelector);
    const karyawan = useSelector(karyawanSelector);

    let location = useLocation().pathname;

    const dispatch = useDispatch();
    const { count: approvalCount, items: approvalItems, loading: approvalLoading } = useApprovalCount();

    const [bellOpen, setBellOpen] = useState(false);
    const bellRef = useRef(null);

    // Tutup popup jika klik di luar
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (bellRef.current && !bellRef.current.contains(e.target)) {
                setBellOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        dispatch(checkLoginUser({}));
        const interval = setInterval(() => {
            dispatch(checkLoginUser({}));
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const userData = JSON.parse(localStorage.getItem("user") || "null") ?? {};

    if (location === "/") {
        if (userData.Tipe === "Karyawan") {
            location = "DASHBOARD";
        } else if (userData.Tipe === "Customer") {
            location = userData.NamaKaryawan ?? "";
        }
    } else {
        location = location.toUpperCase().split("/").slice(1);
    }

    const buildPath = (segments, upToIndex) =>
        "/" + segments.slice(0, upToIndex + 1).map(s => s.toLowerCase()).join("/");

    const handleNotifNavigate = ({ path, state }) => {
        setBellOpen(false);
        if (state) {
            navigate(path, { state });
        } else {
            navigate(path);
        }
    };

    return (
        <nav
            style={{ background: Colors.primary }}
            className={`py-6 px-3 ${userData?.Tipe === "Karyawan" ? "md:ml-64" : ""}`}
        >
            <div className="container max-w-full mx-auto flex items-center justify-between md:pr-8 md:pl-10">
                <div className="md:hidden">
                    <Button
                        color="transparent"
                        buttonType="link"
                        size="lg"
                        iconOnly
                        rounded
                        ripple="light"
                        onClick={() => setShowSidebar("left-0")}
                    >
                        <Icon name="menu" size="2xl" color="white" />
                    </Button>
                    <div
                        className={`absolute top-2 md:hidden ${
                            showSidebar === "left-0" ? "left-64" : "-left-64"
                        } z-50 transition-all duration-300`}
                    >
                        <Button
                            color="transparent"
                            buttonType="link"
                            size="lg"
                            iconOnly
                            rounded
                            ripple="light"
                            onClick={() => setShowSidebar("-left-64")}
                        >
                            <Icon name="close" size="2xl" color="white" />
                        </Button>
                    </div>
                </div>

                <div
                    style={{ color: Colors.primary }}
                    className="flex justify-between items-center w-full"
                >
                    {/* Breadcrumb */}
                    <div className="uppercase tracking-wider mt-1 font-bold text-2xl border-radius rounded-md px-4 py-2 bg-white drop-shadow-lg">
                        <ol className="inline-flex items-center space-x-1 md:space-x-3">
                            {Array.isArray(location) &&
                                location.map((segment, id) => {
                                    if (id >= 3) return null;
                                    const isFirst = id === 0;
                                    const segmentLabel = segment.replaceAll("-", " ");
                                    const targetPath = buildPath(location, id);
                                    return (
                                        <li key={`${segment}-${id}`} className="inline-flex items-center">
                                            {isFirst ? (
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(targetPath)}
                                                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-0"
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                                    </svg>
                                                    {segmentLabel}
                                                </button>
                                            ) : (
                                                <div className="inline-flex items-center text-sm font-medium text-gray-700 cursor-default">
                                                    <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    {segmentLabel}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })
                            }
                            {!Array.isArray(location) && (
                                <li className="inline-flex items-center">
                                    <div className="inline-flex items-center text-sm font-medium text-gray-700 cursor-default">
                                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                        </svg>
                                        {location.replaceAll("-", " ")}
                                    </div>
                                </li>
                            )}
                        </ol>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Bell notification dengan popup */}
                        <div className="relative" ref={bellRef}>
                            <button
                                type="button"
                                onClick={() => setBellOpen((prev) => !prev)}
                                className="relative p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors focus:outline-none"
                                aria-label="Notifikasi approval"
                            >
                                <BiBell
                                    className={`text-2xl text-white ${approvalCount > 0 ? "animate-bell" : ""}`}
                                />
                                {approvalCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1 leading-none shadow">
                                        {approvalCount > 99 ? "99+" : approvalCount}
                                    </span>
                                )}
                            </button>

                            {bellOpen && (
                                <NotificationPopup
                                    items={approvalItems}
                                    loading={approvalLoading}
                                    count={approvalCount}
                                    onClose={() => setBellOpen(false)}
                                    onNavigate={handleNotifNavigate}
                                />
                            )}
                        </div>

                        {/* User dropdown */}
                        <div className="-mr-4">
                            <Dropdown
                                color="transparent"
                                buttonText={
                                    <div className="flex flex-row items-center gap-3">
                                        <div className="flex flex-col items-start leading-tight">
                                            <span className="text-white text-sm font-semibold">{userData.NamaKaryawan ?? ""}</span>
                                            <span className="text-white text-xs opacity-75">{userData.Jabatan ?? ""}</span>
                                        </div>
                                        <BiChevronDown className="text-xl text-white" />
                                    </div>
                                }
                                rounded
                                style={{ padding: 0, color: "transparent" }}
                            >
                                <DropdownItem color="deepOrange">Profile</DropdownItem>
                                <DropdownItem color="deepOrange" onClick={() => navigate("/change-password")}>
                                    Change Password
                                </DropdownItem>
                                <DropdownItem
                                    color="deepOrange"
                                    onClick={() => {
                                        localStorage.removeItem("user");
                                        // Ke /login, bukan reload: rute halaman client hanya
                                        // terdaftar selama login, reload URL yg sama = layar kosong.
                                        window.location.replace("/login");
                                    }}
                                >
                                    Log Out
                                </DropdownItem>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}
